import type {
  LvdProductComplianceFileOutputDocument,
  LvdProductComplianceFilesState,
} from '@/shared/hooks/api/lvdComplianceFiles'
import type { LvdProductComplianceFileEvidenceSlot } from '@/shared/hooks/api/lvdProductComplianceFileAttachments'
import { findFirstIncompleteRequiredStep } from '@/shared/briefing'
import {
  hasConfirmedHarmonisedStandards,
  parseConfirmedHarmonisedStandards,
  type ConfirmedHarmonisedStandard,
} from '@/v2/lib/confirmedHarmonisedStandards'

export { findFirstIncompleteRequiredStep }

export type LvdBriefingStepKey =
  | 'applicability'
  | 'productDescription'
  | 'productIdentification'
  | 'manufacturerInformation'
  | 'productDrawings'
  | 'standardsTestReport'
  | 'examinationsOrCalculations'
  | 'instructionsAndSafetyInformation'
  | 'confirmedStandards'
  | 'euDeclaration'
  | 'productSignOff'

export type LvdBriefingStepStatus = 'done' | 'open' | 'partial' | 'guidance'

export type LvdBriefingStepRequiredness =
  | 'required'
  | 'optional'
  | 'informational'

export type LvdBriefingStep = {
  key: LvdBriefingStepKey
  status: LvdBriefingStepStatus
  requiredness: LvdBriefingStepRequiredness
}

export type LvdLegislationBriefing = {
  steps: LvdBriefingStep[]
}

function findLatestCompletedOutputDocument(
  outputDocuments: LvdProductComplianceFileOutputDocument[]
): LvdProductComplianceFileOutputDocument | null {
  return (
    outputDocuments.find((document) => document.status === 'COMPLETED') ?? null
  )
}

export type ConfirmedLvdStandard = ConfirmedHarmonisedStandard

export function parseConfirmedLvdStandards(
  json: unknown | null
): ConfirmedLvdStandard[] {
  return parseConfirmedHarmonisedStandards(json)
}

function hasConfirmedStandards(confirmedStandardsJson: unknown | null): boolean {
  return hasConfirmedHarmonisedStandards(confirmedStandardsJson)
}

export function lvdBriefingStepRequiredness(
  key: LvdBriefingStepKey
): LvdBriefingStepRequiredness {
  if (key === 'applicability' || key === 'euDeclaration') {
    return 'informational'
  }
  return 'required'
}

function buildStep(
  key: LvdBriefingStepKey,
  status: LvdBriefingStepStatus
): LvdBriefingStep {
  return {
    key,
    status,
    requiredness: lvdBriefingStepRequiredness(key),
  }
}

function slotFilledStatus(hasFiles: boolean): LvdBriefingStepStatus {
  if (hasFiles) {
    return 'done'
  }
  return 'open'
}

export function buildLvdLegislationBriefing(input: {
  hasProductDescription: boolean
  hasProductIdentification: boolean
  hasManufacturerInformation: boolean
  slotFilled: Record<LvdProductComplianceFileEvidenceSlot, boolean>
  files: LvdProductComplianceFilesState
}): LvdLegislationBriefing {
  const hasEuDeclaration =
    findLatestCompletedOutputDocument(input.files.outputDocuments) != null

  let productDescriptionStatus: LvdBriefingStepStatus = 'open'
  if (input.hasProductDescription) {
    productDescriptionStatus = 'done'
  }

  let productIdentificationStatus: LvdBriefingStepStatus = 'open'
  if (input.hasProductIdentification) {
    productIdentificationStatus = 'done'
  }

  let manufacturerInformationStatus: LvdBriefingStepStatus = 'open'
  if (input.hasManufacturerInformation) {
    manufacturerInformationStatus = 'done'
  }

  let confirmedStandardsStatus: LvdBriefingStepStatus = 'open'
  if (hasConfirmedStandards(input.files.product?.confirmedStandardsJson ?? null)) {
    confirmedStandardsStatus = 'done'
  }

  let euDeclarationStatus: LvdBriefingStepStatus = 'open'
  if (hasEuDeclaration) {
    euDeclarationStatus = 'done'
  }

  let productSignOffStatus: LvdBriefingStepStatus = 'open'
  if (input.files.product?.completedAt != null) {
    productSignOffStatus = 'done'
  }

  return {
    steps: [
      buildStep('applicability', 'done'),
      buildStep('productDescription', productDescriptionStatus),
      buildStep('productIdentification', productIdentificationStatus),
      buildStep('manufacturerInformation', manufacturerInformationStatus),
      buildStep('confirmedStandards', confirmedStandardsStatus),
      buildStep(
        'productDrawings',
        slotFilledStatus(input.slotFilled.DRAWINGS_SCHEMAS)
      ),
      buildStep(
        'standardsTestReport',
        slotFilledStatus(input.slotFilled.STANDARDS_TEST_REPORT)
      ),
      buildStep(
        'examinationsOrCalculations',
        slotFilledStatus(input.slotFilled.EXAMINATIONS_OR_CALCULATIONS)
      ),
      buildStep(
        'instructionsAndSafetyInformation',
        slotFilledStatus(input.slotFilled.INSTRUCTIONS_AND_SAFETY_INFORMATION)
      ),
      buildStep('euDeclaration', euDeclarationStatus),
      buildStep('productSignOff', productSignOffStatus),
    ],
  }
}
